import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
  Bread, Egg, Cookie, BowlFood, Hamburger, Carrot, Coffee, ForkKnife, Pizza,
  Wine, Cake, CookingPot, Fish, Hamburger as Burger, Plus, Trash, ArrowUp, ArrowDown,
  FloppyDisk, ArrowCounterClockwise, GlobeHemisphereWest, Eye, CurrencyEur, Warning,
} from "@phosphor-icons/react";

export const ICON_MAP = {
  Bread, Egg, Cookie, BowlFood, Hamburger, Carrot, Coffee, ForkKnife, Pizza,
  Wine, Cake, CookingPot, Fish,
};
const ICON_OPTIONS = Object.keys(ICON_MAP);

function IconPreview({ name, size = 20 }) {
  const C = ICON_MAP[name] || ForkKnife;
  return <C size={size} weight="duotone" />;
}

/** Eine Kategorie-Karte (Label, Icon, Items). */
function CategoryCard({ cat, onChange, onRemove, onMoveUp, onMoveDown, canUp, canDown }) {
  const setField = (k, v) => onChange({ ...cat, [k]: v });
  const setItem = (idx, k, v) => {
    const items = [...(cat.items || [])];
    items[idx] = { ...items[idx], [k]: v };
    setField("items", items);
  };
  const addItem = () => setField("items", [...(cat.items || []), { name: "", price: null }]);
  const removeItem = (idx) => setField("items", (cat.items || []).filter((_, i) => i !== idx));
  const moveItem = (idx, dir) => {
    const items = [...(cat.items || [])];
    const t = idx + dir;
    if (t < 0 || t >= items.length) return;
    [items[idx], items[t]] = [items[t], items[idx]];
    setField("items", items);
  };

  return (
    <div className="border border-slate-200 rounded-xl p-4 bg-white space-y-3" data-testid={`cat-card-${cat.key}`}>
      <div className="flex items-start gap-3">
        <div className="w-11 h-11 rounded-lg bg-indigo-50 text-indigo-600 flex items-center justify-center flex-shrink-0">
          <IconPreview name={cat.icon} size={22} />
        </div>
        <div className="flex-1 grid grid-cols-1 md:grid-cols-[1fr_180px] gap-2">
          <div>
            <Label className="text-xs text-slate-500">Kategorie-Name</Label>
            <Input value={cat.label || ""} onChange={(e) => setField("label", e.target.value)}
                   placeholder="z. B. Hauptgerichte" className="mt-1" data-testid="cat-label-input" />
          </div>
          <div>
            <Label className="text-xs text-slate-500">Icon</Label>
            <Select value={cat.icon || "ForkKnife"} onValueChange={(v) => setField("icon", v)}>
              <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>
                {ICON_OPTIONS.map((n) => (
                  <SelectItem key={n} value={n}>
                    <span className="inline-flex items-center gap-2"><IconPreview name={n} size={16} /> {n}</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="flex flex-col gap-1">
          <Button size="icon" variant="ghost" onClick={onMoveUp}   disabled={!canUp}   title="Nach oben"><ArrowUp size={16}/></Button>
          <Button size="icon" variant="ghost" onClick={onMoveDown} disabled={!canDown} title="Nach unten"><ArrowDown size={16}/></Button>
          <Button size="icon" variant="ghost" onClick={onRemove} className="text-rose-600 hover:bg-rose-50" title="Kategorie löschen"><Trash size={16}/></Button>
        </div>
      </div>

      <div className="pl-14 space-y-2">
        {(cat.items || []).map((it, idx) => (
          <div key={idx} className="flex items-center gap-2" data-testid={`cat-${cat.key}-item-${idx}`}>
            <Input value={it.name || ""} onChange={(e) => setItem(idx, "name", e.target.value)}
                   placeholder="z. B. Spaghetti Bolognese" className="flex-1" />
            <div className="relative w-32">
              <Input type="text" inputMode="decimal" value={it.price ?? ""}
                     onChange={(e) => setItem(idx, "price", e.target.value === "" ? null : e.target.value)}
                     placeholder="Aufpreis" className="pr-8" />
              <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-slate-400">€</span>
            </div>
            <Button size="icon" variant="ghost" onClick={() => moveItem(idx, -1)} disabled={idx === 0} title="Nach oben"><ArrowUp size={14}/></Button>
            <Button size="icon" variant="ghost" onClick={() => moveItem(idx,  1)} disabled={idx === (cat.items.length - 1)} title="Nach unten"><ArrowDown size={14}/></Button>
            <Button size="icon" variant="ghost" onClick={() => removeItem(idx)} className="text-rose-600 hover:bg-rose-50" title="Entfernen"><Trash size={14}/></Button>
          </div>
        ))}
        <Button variant="outline" size="sm" onClick={addItem} className="mt-1" data-testid="cat-add-item-btn">
          <Plus size={14} className="mr-1"/> Zutat / Gericht hinzufügen
        </Button>
        <p className="text-xs text-slate-400 italic">
          Aufpreis leer lassen = Standardpreis (19,99 €). Nur eintragen, wenn dieses Gericht teurer ist.
        </p>
      </div>
    </div>
  );
}

function SectionEditor({ section, data, onChange }) {
  const setSubtitle = (v) => onChange({ ...data, subtitle: v });
  const setCategory = (idx, cat) => {
    const cats = [...(data.categories || [])];
    cats[idx] = cat;
    onChange({ ...data, categories: cats });
  };
  const addCategory = () => onChange({
    ...data,
    categories: [...(data.categories || []), {
      key: `cat_${Date.now()}`, label: "Neue Kategorie", icon: "ForkKnife", items: [],
    }],
  });
  const removeCategory = (idx) => onChange({ ...data, categories: data.categories.filter((_, i) => i !== idx) });
  const moveCategory = (idx, dir) => {
    const cats = [...(data.categories || [])];
    const t = idx + dir;
    if (t < 0 || t >= cats.length) return;
    [cats[idx], cats[t]] = [cats[t], cats[idx]];
    onChange({ ...data, categories: cats });
  };

  return (
    <div className="space-y-4">
      <div>
        <Label className="text-xs text-slate-500">Überschrift / Hinweistext (wird oben im Formular angezeigt)</Label>
        <Textarea value={data.subtitle || ""} onChange={(e) => setSubtitle(e.target.value)}
                  rows={3} className="mt-1" data-testid={`subtitle-${section}`} />
      </div>

      {(data.categories || []).map((cat, idx) => (
        <CategoryCard
          key={cat.key || idx}
          cat={cat}
          onChange={(c) => setCategory(idx, c)}
          onRemove={() => removeCategory(idx)}
          onMoveUp={() => moveCategory(idx, -1)}
          onMoveDown={() => moveCategory(idx,  1)}
          canUp={idx > 0}
          canDown={idx < (data.categories.length - 1)}
        />
      ))}

      <Button variant="outline" onClick={addCategory} data-testid={`add-category-${section}`}>
        <Plus size={16} className="mr-2"/> Kategorie hinzufügen
      </Button>
    </div>
  );
}

export default function MenuEditor() {
  const [catalog, setCatalog] = useState(null);
  const [basePrices, setBasePrices] = useState({ breakfast: 19.99, dinner: 19.99 });
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState("breakfast");

  const load = () => Promise.all([
    api.get("/menu-catalog").then(r => setCatalog(r.data)),
    api.get("/settings/extras-prices").then(r => setBasePrices({
      breakfast: Number(r.data?.breakfast ?? 19.99),
      dinner:    Number(r.data?.dinner    ?? 19.99),
    })),
  ]).catch(() => toast.error("Speisekarte konnte nicht geladen werden"));

  useEffect(() => { load(); }, []);

  // Normalisiert deutsche Zahlen-Eingabe: "17,99" → 17.99, "17.99" → 17.99
  const normalizePrice = (v) => {
    if (v === "" || v === null || v === undefined) return 0;
    const s = String(v).replace(/\s/g, "").replace(",", ".");
    const n = parseFloat(s);
    return isNaN(n) ? 0 : n;
  };

  const save = async () => {
    if (!catalog) return;
    const bfPrice = normalizePrice(basePrices.breakfast);
    const dnPrice = normalizePrice(basePrices.dinner);
    if (bfPrice < 0 || dnPrice < 0) {
      toast.error("Preise müssen positive Zahlen sein.");
      return;
    }
    setSaving(true);
    try {
      const [r1, r2] = await Promise.all([
        api.put("/menu-catalog", catalog),
        api.put("/settings/extras-prices", { breakfast: bfPrice, dinner: dnPrice }),
      ]);
      setCatalog(r1.data);
      setBasePrices({ breakfast: r2.data.breakfast, dinner: r2.data.dinner });
      toast.success(`Gespeichert · Frühstück ${bfPrice.toFixed(2).replace(".", ",")} € · Abendessen ${dnPrice.toFixed(2).replace(".", ",")} €`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Speichern fehlgeschlagen");
    } finally {
      setSaving(false);
    }
  };

  const reset = async () => {
    if (!window.confirm("Standard-Speisekarte wiederherstellen? Deine Änderungen gehen verloren.")) return;
    try {
      const r = await api.post("/menu-catalog/reset");
      setCatalog(r.data);
      toast.success("Standard-Speisekarte wiederhergestellt");
    } catch {
      toast.error("Zurücksetzen fehlgeschlagen");
    }
  };

  const previewBreakfast = `${window.location.origin}/fruehstueck`;
  const previewMeal      = `${window.location.origin}/essen`;

  if (!catalog) return <div className="p-6 text-slate-500">Lade Speisekarte…</div>;

  return (
    <div className="p-4 md:p-6 max-w-5xl mx-auto space-y-6" data-testid="menu-editor-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-foreground" style={{fontFamily:"'Work Sans',sans-serif"}}>
            Speisekarte bearbeiten
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Zutaten & Gerichte werden <strong>live</strong> in die iFrames auf <code>mainspessartblick.de</code> übertragen.
            Standardpreise unten · optionaler Aufpreis pro Gericht möglich.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={reset} data-testid="menu-reset-btn">
            <ArrowCounterClockwise size={16} className="mr-2"/> Zurücksetzen
          </Button>
          <Button onClick={save} disabled={saving} data-testid="menu-save-btn"
                  className="bg-emerald-600 hover:bg-emerald-700 text-white">
            <FloppyDisk size={16} className="mr-2"/> {saving ? "Speichern…" : "Speichern & live schalten"}
          </Button>
        </div>
      </div>

      {/* === STANDARDPREISE === */}
      <div className="border-2 border-emerald-200 bg-emerald-50/50 rounded-xl p-5 space-y-3" data-testid="base-prices-card">
        <div className="flex items-center gap-2">
          <CurrencyEur size={22} weight="duotone" className="text-emerald-700" />
          <h2 className="text-lg font-bold text-slate-800">Standardpreise</h2>
        </div>
        <p className="text-xs text-slate-600 leading-relaxed">
          Diese Preise werden an <strong>drei Stellen</strong> gleichzeitig verwendet:
          im <strong>Warenkorb</strong> (Buchungs-Extras-Dialog), bei automatischen Rechnungsberechnungen
          und bei NIC-Antworten an Gäste. Für einzelne teurere Gerichte kannst du
          unten pro Item einen <strong>Aufpreis</strong> eintragen.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label className="text-xs text-slate-600 font-medium">🥐 Frühstück pro Person</Label>
            <div className="relative mt-1">
              <Input type="text" inputMode="decimal" value={basePrices.breakfast}
                     onChange={(e) => setBasePrices(p => ({ ...p, breakfast: e.target.value }))}
                     className="pr-9 text-base font-semibold" data-testid="base-price-breakfast"
                     placeholder="19,99" />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-slate-500">€</span>
            </div>
          </div>
          <div>
            <Label className="text-xs text-slate-600 font-medium">🍽️ Mittag- & Abendessen pro Person</Label>
            <div className="relative mt-1">
              <Input type="text" inputMode="decimal" value={basePrices.dinner}
                     onChange={(e) => setBasePrices(p => ({ ...p, dinner: e.target.value }))}
                     className="pr-9 text-base font-semibold" data-testid="base-price-dinner"
                     placeholder="19,99" />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-slate-500">€</span>
            </div>
          </div>
        </div>
        <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-900">
          <Warning size={16} weight="fill" className="text-amber-500 flex-shrink-0 mt-0.5" />
          <div>
            <strong>Hinweis:</strong> Der Preis im Hinweistext ("🥐 19,99 € pro Person · 7:00 – 9:00 Uhr …")
            wird <strong>nicht automatisch</strong> aktualisiert — den musst du zusätzlich im jeweiligen Tab
            oben im Textfeld anpassen.
          </div>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="w-full md:w-auto">
          <TabsTrigger value="breakfast" data-testid="tab-breakfast">
            <Bread size={16} className="mr-2"/> Frühstück
          </TabsTrigger>
          <TabsTrigger value="meal" data-testid="tab-meal">
            <ForkKnife size={16} className="mr-2"/> Mittag & Abendessen
          </TabsTrigger>
        </TabsList>

        <TabsContent value="breakfast" className="mt-5 space-y-4">
          <div className="flex items-center gap-2 text-xs text-slate-500">
            <GlobeHemisphereWest size={14}/>
            <span>Vorschau:</span>
            <a href={previewBreakfast} target="_blank" rel="noreferrer" className="text-indigo-600 underline inline-flex items-center gap-1">
              {previewBreakfast} <Eye size={12}/>
            </a>
          </div>
          <SectionEditor section="breakfast" data={catalog.breakfast} onChange={(v) => setCatalog({ ...catalog, breakfast: v })} />
        </TabsContent>

        <TabsContent value="meal" className="mt-5 space-y-4">
          <div className="flex items-center gap-2 text-xs text-slate-500">
            <GlobeHemisphereWest size={14}/>
            <span>Vorschau:</span>
            <a href={previewMeal} target="_blank" rel="noreferrer" className="text-indigo-600 underline inline-flex items-center gap-1">
              {previewMeal} <Eye size={12}/>
            </a>
          </div>
          <SectionEditor section="meal" data={catalog.meal} onChange={(v) => setCatalog({ ...catalog, meal: v })} />
        </TabsContent>
      </Tabs>

      <div className="flex justify-end pt-4 border-t border-slate-200">
        <Button onClick={save} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="menu-save-btn-bottom">
          <FloppyDisk size={16} className="mr-2"/> {saving ? "Speichern…" : "Speichern & live schalten"}
        </Button>
      </div>
    </div>
  );
}
